/* Blog index + detail + shared BlogCard. */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Link } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import RichText from "../../ui/RichText.jsx";
import { Button, Badge, SmartImage, Skeleton, EmptyState, Icon } from "../../ui/index.js";
import { usePosts } from "../../data/hooks.js";
import { formatDate } from "../../utils/format.js";
import NotFoundPage from "./NotFoundPage.jsx";

export function BlogCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="blog-card">
      <div className="blog-card__image">
        <SmartImage src={post.featuredImage?.url} alt={post.featuredImage?.alt || post.title} label={post.title} />
      </div>
      <div className="blog-card__body">
        <div className="blog-card__meta">By {post.authorName} · {formatDate(post.publishedAt, { year: "numeric", month: "long", day: "numeric" })}</div>
        <h3 className="blog-card__title">{post.title}</h3>
        <p className="blog-card__excerpt">{post.excerpt}</p>
      </div>
    </Link>
  );
}

export function BlogIndexPage() {
  const { posts, loading } = usePosts({ publishedOnly: true });
  const [visible, setVisible] = useState(6);
  const shown = posts.slice(0, visible);
  const hasMore = visible < posts.length;

  return (
    <PublicShell>
      <Helmet><title>Blog — Purple Angels Kids Foundation</title></Helmet>
      <section className="page-header">
        <div className="container">
          <h1 className="t-4xl" style={{ margin: 0 }}>Stories & updates</h1>
          <p className="page-header__subtitle">Reflections, lessons, and news from our work.</p>
        </div>
      </section>

      <section style={{ paddingBottom: 96 }}>
        <div className="container">
          {loading ? (
            <div className="blog-grid">{[0, 1, 2].map((i) => <Skeleton key={i} h={320} />)}</div>
          ) : shown.length === 0 ? (
            <EmptyState icon={<Icon.FileText size={40} />} title="No posts yet." body="Check back soon — new pieces go up regularly." />
          ) : (
            <>
              <div className="blog-grid">
                {shown.map((p) => <BlogCard key={p.id} post={p} />)}
              </div>
              <div className="center" style={{ marginTop: 56 }}>
                {hasMore ? (
                  <Button variant="secondary" size="lg" onClick={() => setVisible((v) => v + 6)}>Load more posts</Button>
                ) : (
                  posts.length > 6 && <p className="t-sm muted">You've reached the end.</p>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const { posts, loading } = usePosts({ publishedOnly: true });

  if (loading) {
    return (
      <PublicShell>
        <div className="container" style={{ padding: "64px 0", display: "flex", flexDirection: "column", gap: 16 }}>
          <Skeleton w="60%" h={40} /><Skeleton h={360} /><Skeleton h={200} />
        </div>
      </PublicShell>
    );
  }

  const post = posts.find((p) => p.slug === slug);
  if (!post) return <NotFoundPage />;
  const more = posts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <PublicShell>
      <Helmet><title>{post.title} — Purple Angels Kids Foundation</title></Helmet>
      <article style={{ paddingBottom: 96 }}>
        <div className="container" style={{ paddingTop: 48 }}>
          <Link to="/blog" className="back-link"><Icon.ArrowLeft size={14} />Back to blog</Link>
          <div style={{ aspectRatio: "16 / 9", borderRadius: "var(--radius-xl)", overflow: "hidden", marginTop: 16, marginBottom: 48, maxHeight: 540 }}>
            <SmartImage src={post.featuredImage?.url} alt={post.featuredImage?.alt || post.title} label={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        </div>
        <div className="container">
          <div className="prose">
            <div className="t-sm muted" style={{ marginBottom: 16 }}>
              By {post.authorName} {post.authoredByMinor && <Badge variant="minor" noDot style={{ marginLeft: 8 }}>Authored by a minor</Badge>} · {formatDate(post.publishedAt, { year: "numeric", month: "long", day: "numeric" })}
            </div>
            <h1 className="t-4xl" style={{ marginTop: 0, marginBottom: 32 }}>{post.title}</h1>
            <RichText html={post.bodyHtml} className="" />
          </div>
        </div>
        {more.length > 0 && (
          <>
            <div className="divider divider--dot" />
            <section className="section">
              <div className="container">
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 32 }}>More stories</h2>
                <div className="blog-grid">{more.map((p) => <BlogCard key={p.id} post={p} />)}</div>
              </div>
            </section>
          </>
        )}
      </article>
    </PublicShell>
  );
}

export default BlogIndexPage;
